import { useMemo, useState } from 'react';
import { Bell, Pencil, Search } from 'lucide-react';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import { Button, Input, Skeleton, Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '@vnetwork/ui/primitives';
import { LiveConsoleBreadcrumbs } from '../LiveConsoleBreadcrumbs';
import { Field } from '@vnetwork/ui/forms';
import { DataTable, EmptyState } from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import { useLmsNotificationCallbacks, useUpdateLmsNotificationCallback } from '@/api/media/live-notifications';
import type { LmsNotificationCallback } from '@/api/media/live-notifications-types';
import type { ColumnDef } from '@vnetwork/ui/data';
import styles from './NotificationsList.module.css';

export interface LiveNotificationsProps {
  serviceId: string;
}

/** Notification callback list with inline URL editor per callback entry. */
export function LiveNotifications({ serviceId }: LiveNotificationsProps) {
  const { show } = useToast();

  const { data: callbacks = [], isLoading } = useLmsNotificationCallbacks(serviceId);
  const loading = isLoading;

  const [query, setQuery] = useState('');
  const [editTarget, setEditTarget] = useState<LmsNotificationCallback | null>(null);
  const [urlDraft, setUrlDraft] = useState('');
  const [urlError, setUrlError] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return callbacks;
    return callbacks.filter((c) => c.url.toLowerCase().includes(q) || String(c.id).includes(q));
  }, [callbacks, query]);

  const updateMut = useUpdateLmsNotificationCallback(serviceId, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Callback URL updated' });
      setEditTarget(null);
    },
    onError: (e) => show({ tone: 'danger', title: e.message || 'Failed to update callback' }),
  });

  const openEdit = (cb: LmsNotificationCallback) => {
    setEditTarget(cb);
    setUrlDraft(cb.url);
    setUrlError('');
  };

  const handleSave = () => {
    const trimmed = urlDraft.trim();
    if (!trimmed) { setUrlError('URL is required.'); return; }
    try {
      const u = new URL(trimmed);
      if (u.protocol !== 'http:' && u.protocol !== 'https:') {
        setUrlError('URL must start with http:// or https://');
        return;
      }
    } catch {
      setUrlError('Please enter a valid URL (e.g. https://example.com/webhook)');
      return;
    }
    updateMut.mutate({ id: editTarget!.id, req: { url: trimmed } });
  };

  const columns: ColumnDef<LmsNotificationCallback, unknown>[] = [
    { id: 'id', header: 'ID', accessorKey: 'id' },
    { id: 'url', header: 'Callback URL', accessorKey: 'url' },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <Button variant="ghost" size="sm" startIcon={<Pencil size={14} />} onClick={() => openEdit(row.original)}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<LiveConsoleBreadcrumbs trail={[{ label: 'Notifications' }]} />}
        title={`Notifications${callbacks.length ? ` (${callbacks.length})` : ''}`}
        description="Callback URLs receive real-time stream event notifications."
      />
      <Toolbar
        start={
          <Input
            startSlot={<Search size={15} />}
            placeholder="Search callbacks"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            wrapperClassName={styles.searchInput}
          />
        }
      />
      {loading ? (
        <div className={styles.skeletonGrid}>
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="rect" height={44} />)}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={filtered}
          aria-label="Notification callbacks"
          emptyState={
            // No callbacks at all → first-use copy; a search miss → plain string.
            query.trim() ? (
              'No callbacks match your search.'
            ) : (
              <EmptyState
                icon={<Bell size={20} />}
                size="sm"
                title="No notification callbacks"
                description="Callback URLs appear here once configured for this stream."
              />
            )
          }
          footer={filtered.length ? `Showing ${filtered.length} callback${filtered.length === 1 ? '' : 's'}` : undefined}
        />
      )}

      {editTarget && (
        <Dialog open onOpenChange={(open) => { if (!open) setEditTarget(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Callback URL</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <Field label="Callback URL" required error={urlError}>
                {(fp) => (
                  <Input
                    {...fp}
                    value={urlDraft}
                    onChange={(e) => { setUrlDraft(e.target.value); setUrlError(''); }}
                    placeholder="https://example.com/webhook"
                    autoFocus
                  />
                )}
              </Field>
            </DialogBody>
            <DialogFooter>
              <Button variant="secondary" onClick={() => setEditTarget(null)} disabled={updateMut.isPending}>Cancel</Button>
              <Button onClick={handleSave} disabled={updateMut.isPending}>
                {updateMut.isPending ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Container>
  );
}
